import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowUpRight, ArrowLeft, Copy, CheckCircle2, Sparkles,
  Code, Target, TrendingUp, Heart, Calculator, Megaphone,
  ClipboardList, MessageSquare, Palette, BarChart3, Users,
  GraduationCap, Settings, Scale, FileText,
  HardHat, Stethoscope, Pill, Building2, Bug, Landmark, Plane,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { ROLE_LIBRARY } from "@/lib/roleLibrary";

const ICONS = {
  Code, Target, TrendingUp, Heart, Calculator, Megaphone, ClipboardList, MessageSquare,
  Palette, BarChart3, Users, GraduationCap, Settings, Scale,
  HardHat, Stethoscope, Pill, Building2, Bug, Landmark, Plane,
};

export default function RoleTemplate() {
  const { slug } = useParams();
  const role = ROLE_LIBRARY.find((r) => r.slug === slug);
  if (!role) return <Navigate to="/templates" replace />;
  const Icon = ICONS[role.icon] || FileText;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const url = `https://resumelab.co.ke/templates/role/${role.slug}`;
  const seoJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": `${role.name} Resume — Bullets, Summary & Skills (2026)`,
        "description": `${role.bullets.length} quantified, recruiter-tested ${role.name} resume bullets, summary starters, and a skills checklist. Free. Copy-paste ready.`,
        "url": url,
        "isPartOf": { "@type": "WebSite", "url": "https://resumelab.co.ke", "name": "ResumeLab" },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://resumelab.co.ke/" },
          { "@type": "ListItem", "position": 2, "name": "Templates", "item": "https://resumelab.co.ke/templates" },
          { "@type": "ListItem", "position": 3, "name": role.name, "item": url },
        ],
      },
      {
        "@type": "ItemList",
        "name": `${role.name} resume bullets`,
        "numberOfItems": role.bullets.length,
        "itemListElement": role.bullets.map((b, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": b,
        })),
      },
    ],
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50" data-testid="role-template-page">
      <SEO
        title={`${role.name} Resume Bullets — ${role.bullets.length} Quantified Examples (2026) | ResumeLab`}
        description={`Free copy-paste ${role.name} resume bullets, summary starters, and skills list. ${role.bullets.length} recruiter-tested, quantified examples. ATS-friendly.`}
        url={url}
        jsonLd={seoJsonLd}
      />

      <section className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <Link to="/templates" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8" data-testid="role-back-link">
            <ArrowLeft className="w-4 h-4" /> All templates
          </Link>

          <div className="flex items-start gap-5 mb-8">
            <div className="w-14 h-14 bg-zinc-900 text-white grid place-items-center shrink-0">
              <Icon className="w-7 h-7" />
            </div>
            <div>
              <div className="overline mb-2">◷ {role.name.toUpperCase()} RESUME — 2026</div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-zinc-900">
                {role.name}<br />
                <span className="text-[#EF4444]">resume bullets that get callbacks.</span>
              </h1>
            </div>
          </div>

          <p className="text-zinc-600 text-base lg:text-lg leading-relaxed max-w-3xl">{role.blurb}</p>

          <div className="mt-10 flex flex-wrap gap-3" data-testid="role-hero-cta">
            <Link to="/builder" className="inline-flex items-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white px-6 py-3 text-sm font-semibold transition-colors">
              Build my {role.name.toLowerCase()} resume <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/evaluate" className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-900 px-6 py-3 text-sm font-semibold transition-colors">
              Audit my existing resume
            </Link>
          </div>
        </div>
      </section>

      {/* Summary starters */}
      <section className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="overline mb-3">◷ SUMMARY STARTERS</div>
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-zinc-900 mb-8">
            Lead with this.
          </h2>
          <div className="space-y-4" data-testid="role-summary-starters">
            {role.summary_starters.map((s, i) => (
              <div key={i} className="group flex items-start gap-4 bg-zinc-50 border-l-2 border-zinc-300 hover:border-[#EF4444] transition-colors p-5">
                <span className="flex-1 text-zinc-700 leading-relaxed text-base">{s}</span>
                <button type="button" onClick={() => copy(s)} className="text-zinc-400 hover:text-zinc-900 transition-colors opacity-0 group-hover:opacity-100" aria-label="Copy">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bullet library */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="overline mb-3">◆ BULLET LIBRARY · {role.bullets.length}</div>
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-zinc-900 mb-3">
            {role.bullets.length} quantified bullets.
          </h2>
          <p className="text-zinc-600 leading-relaxed max-w-2xl mb-10">
            Each one starts with an action verb and ends with a number. Copy them in and replace the metrics with your actual outcomes.
          </p>

          <ul className="space-y-3" data-testid="role-bullets">
            {role.bullets.map((b, i) => (
              <li key={i} className="group flex items-start gap-4 bg-white border border-zinc-200 hover:border-zinc-400 transition-colors p-5">
                <span className="font-mono text-xs text-zinc-400 pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1 text-zinc-700 leading-relaxed">{b}</span>
                <button type="button" onClick={() => copy(b)} data-testid={`role-bullet-copy-${i}`} className="text-zinc-400 hover:text-zinc-900 transition-colors opacity-0 group-hover:opacity-100 shrink-0" aria-label="Copy bullet">
                  <Copy className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Skills */}
      <section className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="overline mb-3">◇ SKILLS CHECKLIST</div>
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-zinc-900 mb-8">
            Match the JD. Skip the fluff.
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="role-skills">
            {role.skills.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 bg-zinc-900 text-white px-3 py-1.5 text-sm font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-link to other roles */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="overline mb-3">◧ OTHER ROLES</div>
          <h2 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight text-zinc-900 mb-8">
            Browse more bullet libraries
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {ROLE_LIBRARY.filter((r) => r.slug !== role.slug).slice(0, 8).map((r) => (
              <Link key={r.slug} to={`/templates/role/${r.slug}`} className="bg-zinc-50 hover:bg-zinc-900 hover:text-white border border-zinc-200 hover:border-zinc-900 p-4 transition-colors group">
                <div className="font-semibold text-sm">{r.name}</div>
                <div className="text-xs text-zinc-500 group-hover:text-zinc-300 mt-1">{r.bullets.length} bullets →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-[#EF4444] mb-4" />
          <h2 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tight">
            Bullets help.<br />
            <span className="text-[#EF4444]">A finished resume helps more.</span>
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mt-6 leading-relaxed">
            Drop into ResumeLab&apos;s wizard. Pick a template. Get a recruiter-tested {role.name} CV in 10 minutes — free.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/builder" className="inline-flex items-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white px-8 py-4 text-base font-semibold transition-colors" data-testid="role-bottom-cta">
              Build my resume free <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/templates" className="inline-flex items-center gap-2 border border-zinc-700 hover:bg-zinc-800 text-white px-8 py-4 text-base font-semibold transition-colors">
              Browse all templates
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
